import type { Rng } from '../../core/rng';
import type { Ring, Vec3 } from '../../geo/types';
import { distancePointSegment, pointInRing } from '../../geo/polygon';
import type { RoofProfile } from '../fabric/roofProfile';

export interface RoofBird {
  readonly perch: Vec3;
  readonly yaw: number;
  readonly altitude: number;
  readonly radius: number;
  readonly rest: number;
  readonly lift: number;
  readonly orbit: number;
  readonly period: number;
  readonly phase: number;
  readonly scale: number;
}

export interface RoofBirdPose {
  readonly position: Vec3;
  readonly yaw: number;
  readonly bank: number;
  readonly wing: number;
  readonly fold: number;
}

function ease(value: number): number {
  const u = Math.max(0, Math.min(1, value));
  return u * u * u * (u * (u * 6 - 15) + 10);
}

function perchOn(roof: RoofProfile): Vec3 | null {
  const length = Math.hypot(roof.to[0] - roof.from[0], roof.to[2] - roof.from[2]);
  for (const fraction of [0.5, 0.22, 0.78, 0.36, 0.64]) {
    if (Math.min(fraction, 1 - fraction) * length < 1.5) continue;
    const at = roof.from.map((v, i) => v + (roof.to[i]! - v) * fraction) as unknown as Vec3;
    if (roof.chimneys.every((p) => Math.hypot(p[0] - at[0], p[2] - at[2]) > 1.15)) return at;
  }
  return null;
}

function orbitPoint(perch: Vec3, yaw: number, radius: number, angle: number): Vec3 {
  const forward = radius * Math.sin(angle),
    sideways = radius * (1 - Math.cos(angle));
  return [
    perch[0] + Math.cos(yaw) * forward + Math.sin(yaw) * sideways,
    perch[1],
    perch[2] - Math.sin(yaw) * forward + Math.cos(yaw) * sideways,
  ];
}

/** Small flights rise above nearby roofs before turning; tall civic footprints stay outside their orbits. */
export function planRoofBirds(
  roofs: readonly RoofProfile[],
  excluded: readonly Ring[],
  rng: Rng
): readonly RoofBird[] {
  const birds: RoofBird[] = [];
  for (const roof of rng.shuffle(roofs)) {
    const perch = perchOn(roof);
    if (!perch || birds.some((b) => Math.hypot(b.perch[0] - perch[0], b.perch[2] - perch[2]) < 24))
      continue;
    const yaw = Math.atan2(roof.from[2] - roof.to[2], roof.to[0] - roof.from[0]);
    const radius = rng.range(6, 9);
    const orbit = Array.from({ length: 33 }, (_, i) =>
      orbitPoint(perch, yaw, radius, (i * Math.PI) / 16)
    );
    if (
      orbit.some((p) =>
        excluded.some((ring) => {
          const at = [p[0], -p[2]] as const;
          return (
            pointInRing(at, ring) ||
            ring.some(
              (from, i) =>
                distancePointSegment(at, { a: from, b: ring[(i + 1) % ring.length]! }) < 1
            )
          );
        })
      )
    )
      continue;
    const near = roofs.filter(
      (r) =>
        Math.min(
          Math.hypot(r.from[0] - perch[0], r.from[2] - perch[2]),
          Math.hypot(r.to[0] - perch[0], r.to[2] - perch[2])
        ) <
        2 * radius + Math.hypot(r.to[0] - r.from[0], r.to[2] - r.from[2]) + 3
    );
    const altitude = Math.max(perch[1] + 9, ...near.map((r) => r.from[1] + 5));
    const lift = Math.max(6, (altitude - perch[1]) / 3);
    const rest = rng.range(22, 43),
      flying = rng.range(22, 30);
    const period = rest + 2 * lift + flying;
    birds.push({
      perch,
      yaw,
      radius,
      altitude,
      lift,
      rest,
      orbit: flying,
      period,
      phase: rng.range(0, period),
      scale: rng.range(0.96, 1.1),
    });
    if (birds.length === 144) break;
  }
  return birds;
}

/** A nightfall lets an airborne bird finish its landing before it roosts. */
export function roofBirdPoseAt(
  bird: RoofBird,
  time: number,
  stopFlightsSince = Infinity
): RoofBirdPose {
  const elapsed = time + bird.phase;
  const cycle = Math.floor(elapsed / bird.period);
  const stage = elapsed - cycle * bird.period;
  const liftAt = cycle * bird.period + bird.rest - bird.phase;
  const foot = bird.perch[1] + 0.14 * bird.scale;
  const perched: RoofBirdPose = {
    position: [bird.perch[0], foot, bird.perch[2]],
    yaw: bird.yaw,
    bank: 0,
    wing: 0.012 * Math.sin(time * 1.7 + bird.phase),
    fold: 1,
  };
  if (stage < bird.rest || liftAt >= stopFlightsSince) return perched;
  const up = stage - bird.rest,
    cruise = up - bird.lift,
    down = cruise - bird.orbit;
  const flap = Math.sin(time * 7.5 + bird.phase) * 0.72;
  if (cruise < 0) {
    const u = up / bird.lift;
    const fold = 1 - ease(up / 0.9);
    return {
      position: [bird.perch[0], foot + (bird.altitude - foot) * ease(u), bird.perch[2]],
      yaw: bird.yaw,
      bank: 0,
      wing: flap * (1 - fold),
      fold,
    };
  }
  if (down < 0) {
    const angle = Math.PI * 2 * ease(cruise / bird.orbit);
    const p = orbitPoint(bird.perch, bird.yaw, bird.radius, angle);
    return {
      position: [p[0], bird.altitude, p[2]],
      yaw: bird.yaw - angle,
      bank: -0.17 * Math.sin((Math.PI * cruise) / bird.orbit),
      wing: flap,
      fold: 0,
    };
  }
  const fold = ease((down - bird.lift + 0.9) / 0.9);
  return {
    position: [
      bird.perch[0],
      bird.altitude + (foot - bird.altitude) * ease(down / bird.lift),
      bird.perch[2],
    ],
    yaw: bird.yaw - Math.PI * 2,
    bank: 0,
    wing: flap * (1 - fold),
    fold,
  };
}
